<template>
	<!-- Tema "kpis": misma identidad violeta (charts + filtros) que el tab
		KPIs de Estado de Resultados. -->
	<div class="balance_kpis_tab report_theme_container" data-theme="kpis">
		<TabHeader
			title="KPIs"
			description="Indicadores de tu balance"
			:filterPeriod="filterPeriod"
			:filterOptions="filterOptions"
			:endMonth="endMonth"
			:getNow="getNow"
			calendarMode="range"
			:dateRange="calendarRange"
			:year-range="yearRange"
			@update-filter-period="updateFilterPeriod"
			@update-end-month="updateEndMonth"
			@update-date-range="updateDateRange"
			@update-year-range="updateYearRange">
			<!-- Filtro central (spec "Graficos KPIs Balance"): Liquidez y
				Solvencia, cada grupo con sus 4 gráficos. -->
			<template #center-sub-nav>
				<div class="sub_nav_filters">
					<button v-for="group in kpiGroups" :key="group.id"
						type="button"
						class="filter_btn"
						:class="{ 'is-active': activeSubFilter === group.id }"
						@click="updateActiveSubFilter(group.id)">
						{{ group.label }}
					</button>
				</div>
			</template>
		</TabHeader>

		<!-- Grilla 2×2 del grupo activo (fases 1+2 de
			docs/DECISIONES.md §8). Solo Solvencia G3 (carga
			financiera) es placeholder "Próximamente": sus fórmulas siguen
			en definición y sin ellas el gráfico no aporta. -->
		<div class="kpi_grid mt-2">
			<article v-for="card in activeCards" :key="card.id"
				class="kpi_card"
				:class="{ 'kpi_card--placeholder': !card.implemented }">
				<template v-if="card.implemented">
					<header class="kpi_card_header">
						<span class="kpi_card_title">{{ card.title }}</span>
						<!-- Switch del card ("Botón Días" de la spec): dos lentes
							sobre series ya presentes en la respuesta — sin refetch. -->
						<div v-if="card.toggle"
							class="kpi_switch"
							role="group"
							:aria-label="'Vista de ' + card.title">
							<span class="kpi_switch_thumb"
								:class="{ 'is-right': toggles[card.id] }"
								aria-hidden="true"></span>
							<button type="button"
								class="kpi_switch_option"
								:class="{ 'is-active': !toggles[card.id] }"
								:aria-pressed="!toggles[card.id] ? 'true' : 'false'"
								@click="toggles[card.id] = false">
								{{ card.toggle[0] }}
							</button>
							<button type="button"
								class="kpi_switch_option"
								:class="{ 'is-active': toggles[card.id] }"
								:aria-pressed="toggles[card.id] ? 'true' : 'false'"
								@click="toggles[card.id] = true">
								{{ card.toggle[1] }}
							</button>
						</div>
					</header>
					<div class="kpi_card_body">
						<p v-if="error" class="kpi_state kpi_state--error">{{ error }}</p>
						<p v-else-if="isLoading" class="kpi_state">Cargando…</p>
						<p v-else-if="!hasKpisData" class="kpi_state">
							Sin datos para el período seleccionado.
						</p>
						<KpiComposedChart v-else
							:monthHeaders="kpisData.monthHeaders"
							:series="card.series" />
					</div>
				</template>
				<div v-else class="kpi_state">
					<span class="kpi_placeholder_title">{{ card.title }}</span>
					Próximamente
				</div>
			</article>
		</div>
	</div>
</template>

<script>
import moment from "moment";
import "moment/locale/es";
import { mapState, mapActions, mapMutations } from "vuex";
import TabHeader from "@/components/financialReports/TabHeader.vue";
import KpiComposedChart from "@/components/financialReports/charts/KpiComposedChart.vue";
import { FILTER_PERIOD_OPTIONS } from "@/config/reportStructure";
import { displayMonthRange, displayYearRange } from "@/store/financialReports/utils/dateRange";

// Grupos del filtro central (spec de la hoja "Graficos KPIs Balance").
const KPI_GROUPS = [
	{ id: "liquidez", label: "Liquidez" },
	{ id: "solvencia", label: "Solvencia" },
];

export default {
	name: "BalanceKpisTab",

	components: {
		TabHeader,
		KpiComposedChart,
	},

	data() {
		return {
			filterOptions: FILTER_PERIOD_OPTIONS,
			kpiGroups: KPI_GROUPS,
			activeSubFilter: "liquidez",
			// Toggle activo de cada card (false = variante base). Declarados
			// upfront — sin Vue.set (regla de migración).
			toggles: {
				lg2: false,
			},
		};
	},

	computed: {
		...mapState("kpisBalance", ["kpisData", "isLoading", "error"]),
		// Filtros de tiempo COMPARTIDOS con el resto de los tabs del reporte
		// (mismo wiring que KpisTab): granularidad en `financialReports`,
		// mes/rangos en `reportsFilters`.
		...mapState("reportsFilters", [
			"dataFirstMonth",
			"endMonth", "selectedPeriodRange", "customRange", "customYearRange",
		]),
		...mapState("financialReports", ["filterPeriod"]),

		getNow() {
			return moment().toDate();
		},

		calendarRange() {
			return displayMonthRange(this.endMonth, this.customRange, this.dataFirstMonth);
		},

		yearRange() {
			return displayYearRange(this.endMonth, this.customYearRange, this.dataFirstMonth);
		},

		hasKpisData() {
			return Boolean(
				this.kpisData
				&& this.kpisData.group === this.activeSubFilter
				&& this.kpisData.monthHeaders.length > 0,
			);
		},

		activeCards() {
			if (this.activeSubFilter === "solvencia") return this.solvenciaCards;
			return this.liquidezCards;
		},

		// Cards del grupo Liquidez en el orden de la spec (G1-G4), todos
		// implementados (fase 2 — Cuenta X): las series por concepto suman
		// solo cuentas con Cuenta X asignada.
		liquidezCards() {
			const diasToggle = ["Monto $", "Días"];
			if (!this.hasKpisData) {
				return [
					{ id: "lg1", title: "Caja y equivalentes", implemented: true },
					{ id: "lg2", title: "Cuentas por cobrar, pagar e inventario", implemented: true, toggle: diasToggle },
					{ id: "lg3", title: "Razones de liquidez", implemented: true },
					{ id: "lg4", title: "Capital de trabajo", implemented: true },
				];
			}
			const { capitalTrabajo, caja, cuentas, razones } = this.kpisData;
			// "Botón Días" (spec 2026-07-31): la lente alterna muestra los
			// días clásicos saldo/flujo × días del período (DSO/DPO/DIO),
			// calculados en el backend.
			const cuentasSeries = this.toggles.lg2
				? [
					{ name: "Días por Cobrar", type: "line", unit: "count", data: cuentas.diasCobrar },
					{ name: "Días por Pagar", type: "line", unit: "count", data: cuentas.diasPagar },
					{ name: "Días de Inventario", type: "line", unit: "count", data: cuentas.diasInventario },
				]
				: [
					{ name: "Cuentas por Cobrar", type: "bar", unit: "money", stack: "arriba", data: cuentas.cuentasPorCobrar },
					{ name: "Inventario", type: "bar", unit: "money", stack: "arriba", data: cuentas.inventario },
					{ name: "Cuentas por Pagar", type: "bar", unit: "money", data: cuentas.cuentasPorPagar, down: true },
				];
			return [
				{
					id: "lg1",
					title: "Caja y equivalentes",
					implemented: true,
					series: [
						{ name: "Caja", type: "bar", unit: "money", stack: "caja", data: caja.caja },
						{ name: "Equivalentes de Caja", type: "bar", unit: "money", stack: "caja", data: caja.equivalentes },
						{ name: "Caja + Eq. / Activos %", type: "line", unit: "percent", data: caja.cajaSobreActivosPct },
					],
				},
				{
					id: "lg2",
					title: "Cuentas por cobrar, pagar e inventario",
					implemented: true,
					toggle: diasToggle,
					series: cuentasSeries,
				},
				{
					id: "lg3",
					title: "Razones de liquidez",
					implemented: true,
					series: [
						{ name: "Razón Corriente", type: "line", unit: "ratio", data: razones.razonCorriente },
						{ name: "Razón Ácida", type: "line", unit: "ratio", data: razones.razonAcida },
						{ name: "Ciclo de Conversión (días)", type: "bar", unit: "count", data: razones.cicloConversion },
					],
				},
				{
					id: "lg4",
					title: "Capital de trabajo",
					implemented: true,
					series: [
						{ name: "Activo Circulante", type: "bar", unit: "money", data: capitalTrabajo.activoCirculante },
						{ name: "Pasivo Circulante", type: "bar", unit: "money", data: capitalTrabajo.pasivoCirculante, down: true },
						{ name: "Capital de Trabajo", type: "line", unit: "money", data: capitalTrabajo.capitalTrabajo },
					],
				},
			];
		},

		// Cards del grupo Solvencia (G1-G4 de la spec). G2 (deuda financiera)
		// llegó con la fase 2 (Cuenta X). G3 (carga financiera) va como
		// placeholder "Próximamente": sus fórmulas (LTM/EBITDA y /Ventas)
		// siguen en definición y con solo Intereses/Ventas el gráfico no
		// aporta info relevante.
		solvenciaCards() {
			if (!this.hasKpisData) {
				return [
					{ id: "sg1", title: "Ratios de solvencia", implemented: true },
					{ id: "sg2", title: "Deuda financiera", implemented: true },
					{ id: "sg3", title: "Carga financiera", implemented: false },
					{ id: "sg4", title: "ROA y ROE", implemented: true },
				];
			}
			const { ratios, deudaFinanciera, rentabilidad } = this.kpisData;
			return [
				{
					id: "sg1",
					title: "Ratios de solvencia",
					implemented: true,
					series: [
						{ name: "Pasivos / Activos", type: "line", unit: "ratio", data: ratios.pasivosActivos },
						{ name: "Pasivos / Patrimonio", type: "line", unit: "ratio", data: ratios.pasivosPatrimonio },
						{ name: "Patrimonio / Activos", type: "line", unit: "ratio", data: ratios.patrimonioActivos },
					],
				},
				{
					id: "sg2",
					title: "Deuda financiera",
					implemented: true,
					series: [
						{ name: "Deuda Financiera Neta", type: "bar", unit: "money", data: deudaFinanciera.neta },
						{ name: "Deuda Financiera Total", type: "bar", unit: "money", data: deudaFinanciera.total },
						{ name: "DF CP / Total %", type: "line", unit: "percent", data: deudaFinanciera.cpSobreTotalPct },
						{ name: "DF LP / Total %", type: "line", unit: "percent", data: deudaFinanciera.lpSobreTotalPct },
					],
				},
				{ id: "sg3", title: "Carga financiera", implemented: false },
				{
					id: "sg4",
					title: "ROA y ROE",
					implemented: true,
					series: [
						{ name: "ROA %", type: "line", unit: "percent", data: rentabilidad.roaPct },
						{ name: "ROE %", type: "line", unit: "percent", data: rentabilidad.roePct },
					],
				},
			];
		},

		// Firma reactiva: un solo trigger de fetch aunque cambien varios
		// filtros en la misma transacción.
		fetchSignature() {
			return [
				this.endMonth,
				this.selectedPeriodRange,
				this.filterPeriod,
				this.activeSubFilter,
				this.customRange && this.customRange.from,
				this.customRange && this.customRange.to,
				this.customYearRange && this.customYearRange.from,
				this.customYearRange && this.customYearRange.to,
			].join("|");
		},
	},

	methods: {
		...mapActions("kpisBalance", ["fetchKpisBalance"]),
		...mapActions("reportsFilters", ["setCustomRange", "setCustomYearRange"]),
		...mapMutations("reportsFilters", ["SET_END_MONTH"]),
		...mapMutations("financialReports", ["SET_FILTER_PERIOD"]),

		loadCharts() {
			this.fetchKpisBalance({
				endMonth: this.endMonth,
				selectedPeriodRange: this.selectedPeriodRange,
				customRange: this.customRange,
				customYearRange: this.customYearRange,
				period: this.filterPeriod,
				group: this.activeSubFilter,
			});
		},
		updateActiveSubFilter(group) {
			this.activeSubFilter = group;
		},
		updateEndMonth(date) {
			this.SET_END_MONTH(date);
		},
		updateDateRange(range) {
			this.setCustomRange(range);
		},
		updateYearRange(range) {
			this.setCustomYearRange(range);
		},
		updateFilterPeriod(period) {
			this.SET_FILTER_PERIOD(period);
		},
	},

	mounted() {
		this.loadCharts();
	},

	watch: {
		fetchSignature() {
			this.loadCharts();
		},
	},
};
</script>

<style scoped lang="scss">
.balance_kpis_tab {
	position: relative;
	height: 100%;
}

// Misma grilla, cards y sub-filters que KpisTab (los estilos scoped no se
// comparten entre SFCs — mismo markup, mismas classes).
.kpi_grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	grid-auto-rows: 340px;
	gap: 16px;
	width: 100%;

	@media (max-width: 1024px) {
		grid-template-columns: 1fr;
	}
}

.kpi_card {
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 16px;
	box-shadow: var(--report-shadow-card);
	padding: 12px;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

.kpi_card_header {
	flex-shrink: 0;
	margin-bottom: 8px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
}

.kpi_card_title {
	color: var(--report-text);
	font-size: 14px;
	font-weight: 600;
}

.kpi_card_body {
	flex: 1;
	min-height: 0;
	position: relative;
	display: flex;
	align-items: stretch;
	justify-content: stretch;
}

// Switch del card ("Botón Días" de la spec): mismo segmentado en miniatura
// que KpisTab (estilos scoped no se comparten entre SFCs).
.kpi_switch {
	flex-shrink: 0;
	position: relative;
	display: inline-grid;
	grid-template-columns: 1fr 1fr;
	padding: 2px;
	border: 1px solid var(--report-border);
	border-radius: 999px;
	background: var(--report-surface);
}

.kpi_switch_thumb {
	position: absolute;
	top: 2px;
	bottom: 2px;
	left: 2px;
	width: calc(50% - 2px);
	border-radius: 999px;
	background: var(--report-filter-accent, var(--report-primary));
	transition: transform 0.18s ease;

	&.is-right {
		transform: translateX(100%);
	}

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}
}

.kpi_switch_option {
	position: relative;
	z-index: 1;
	padding: 3px 12px;
	border: none;
	border-radius: 999px;
	background: transparent;
	font-size: 12px;
	font-weight: 600;
	font-family: inherit;
	color: var(--report-text-muted-strong);
	cursor: pointer;
	white-space: nowrap;
	transition: color 0.18s ease;

	&:hover {
		color: var(--report-text);
	}

	&.is-active {
		color: var(--report-bg);
	}

	&:focus-visible {
		outline: 2px solid var(--report-filter-accent, var(--report-primary));
		outline-offset: 2px;
	}
}

.kpi_card--placeholder {
	align-items: center;
	justify-content: center;
	border-style: dashed;
}

.kpi_state {
	margin: auto;
	color: var(--report-text-muted);
	font-size: 13px;
	text-align: center;

	&--error {
		color: var(--report-negative);
	}
}

// Título del card postergado, arriba del "Próximamente" — deja claro QUÉ
// gráfico va en esa posición de la spec.
.kpi_placeholder_title {
	display: block;
	margin-bottom: 4px;
	color: var(--report-text-muted-strong);
	font-weight: 600;
}

.sub_nav_filters {
	display: flex;
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	width: fit-content;
}

.filter_btn {
	padding: 6px 16px;
	border: none;
	background: transparent;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	font-family: inherit;
	color: var(--report-text-muted-strong);
	cursor: pointer;
	transition: all 0.2s;

	&:hover {
		background: var(--report-filter-wash, var(--report-surface));
	}

	&.is-active {
		background: var(--report-filter-accent, var(--report-primary));
		color: var(--report-bg);

		&:hover {
			background: var(--report-filter-accent-hover, var(--report-primary-hover));
		}
	}
}
</style>
